import { type FC, useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { trackEvent } from 'dibs-tracking';
import { graphql, useFragment } from 'react-relay';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient.ts';
import { usePdpSharedTracking } from '../../hooks/usePdpSharedTracking.ts';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader.js';
import { useRespUnavailableContext } from '../PdpRespUnavailableContext/PdpRespUnavailableContext.tsx';
import { useDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage.ts';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes.ts';
import { getQueryParam } from '../../../utils/uriUtils.ts';
import scrollIntoView from 'scroll-into-view';
import { BUYER_INCENTIVES } from '../../helpers/tabConstants.ts';
import {
    PDP_SOLD_TOP_BILLBOARD_PLACEMENT,
    PDP_SOLD_BOTTOM_BILLBOARD_PLACEMENT,
} from 'dibs-google-ad-manager/exports/constants.ts';
import { trackBannerAdsAbTestVariant } from 'dibs-google-ad-manager/exports/abTestHelpers.ts';
import { trackPdpBottomNewSlModuleTest } from '../../../utils/abTestHelper.ts';

// components
import { PriceDisplay } from 'dibs-buyer-price';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense.tsx';
import { PdpRespSharedTitle } from '../../respShared/PdpRespSharedTitle/PdpRespSharedTitle.tsx';
import { RecentlyViewedItemsLazy } from 'dibs-recent-history/exports/RecentlyViewedItemsLazy.ts';
import { SharedRespLinksBlock } from '../../../shared/SharedRespLinksBlock/SharedRespLinksBlock.tsx';
import {
    RecentlyViewedItemsProvider,
    TABLET,
    DESKTOP,
} from 'dibs-recent-history/exports/RecentlyViewedItemsProvider.ts';
import { PdpRespSharedBreadcrumbs } from '../../respShared/PdpRespSharedBreadcrumbs/PdpRespSharedBreadcrumbs.tsx';
import { youMayAlsoLike } from '../../../shared/sharedMessages.jsx';
import {
    PdpRespUnavailableDetailsTabs,
    useUnavailableDetailTabsState,
} from '../PdpRespUnavailableDetailsTabs/PdpRespUnavailableDetailsTabs.tsx';
import { PdpSharedEditorialArticles } from '../../shared/PdpSharedEditorialArticles/PdpSharedEditorialArticles.tsx';
import { PdpRespSharedHeroCarousel } from '../../respShared/PdpRespSharedHeroCarousel/PdpRespSharedHeroCarousel.tsx';
import { PdpRespSharedSellerChatLoader } from '../../respShared/PdpRespSharedSellerChatLoader/PdpRespSharedSellerChatLoader.tsx';
import { PdpRespSharedPrintDetailsLazy } from '../../respShared/PdpRespSharedPrintDetails/PdpRespSharedPrintDetailsLazy.tsx';
import { PdpRespSharedQrCodeLazy } from '../../respShared/PdpRespSharedQrCode/PdpRespSharedQrCodeLazy.tsx';
import { BrowserHistoryContextProvider } from '../../../global/BrowserHistoryContext/BrowserHistoryContext.jsx';
import { PdpSharedSellerItemsCarousel } from '../../shared/PdpSharedSellerItemsCarousel/PdpSharedSellerItemsCarousel.tsx';
import { PdpRespRefetchableSimilarItemsGrid } from '../../PdpRespSimilarItems/PdpRespRefetchableSimilarItemsGrid.tsx';
import { SharedBillboardAd } from '../../../shared/SharedBillboardAd/SharedBillboardAd.tsx';
import { PdpSharedFeaturedRelatedItemsCarouselLazy } from '../../PdpFeaturedRelatedItemsCarousel/PdpSharedFeaturedRelatedItemsCarouselLazy.tsx';
import { PdpRespFeaturedItemsGridLazy } from '../../respShared/PdpRespFeaturedItemsGrid/PdpRespFeaturedItemsGridLazy.tsx';
import { PdpSharedRecentlyFavoritedCarouselRenderer } from '../../shared/PdpSharedRecentlyFavoritedCarousel/PdpSharedRecentlyFavoritedCarouselRenderer.tsx';

// styles
import styles from './PdpRespUnavailableContainer.scss';
import dibsCss from 'dibs-css';

// types
import { type PdpRespUnavailableContainer_item$key as Item } from './__generated__/PdpRespUnavailableContainer_item.graphql.ts';
import { type PdpRespUnavailableContainer_user$key as User } from './__generated__/PdpRespUnavailableContainer_user.graphql.ts';
import { type PdpRespUnavailableContainer_viewer$key as Viewer } from './__generated__/PdpRespUnavailableContainer_viewer.graphql.ts';

// lazy components
const PdpSharedItemFavorites = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespUnavailableContainerLazy" */ '../../shared/PdpSharedItemFavorites/PdpSharedItemFavorites.tsx'
        )
);

const PdpRespSharedHeroCarouselPrint = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespUnavailableContainerLazy" */ '../../respShared/PdpRespSharedHeroCarouselPrint/PdpRespSharedHeroCarouselPrint.tsx'
        )
);

const PdpRespSharedSidebarSellerInfoAndContact = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespSharedSidebarSellerInfoAndContact" */ '../../PdpRespSidebarSellerInfo/PdpRespSharedSidebarSellerInfoAndContact.tsx'
        )
);

const PdpSharedUnavailableViewPrice = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedUnavailableViewPrice" */ '../../PdpSharedUnavailableViewPrice/PdpSharedUnavailableViewPrice.tsx'
        )
);

const ViewPriceToast = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "ViewPriceToast" */ 'dibs-buyer-layout/exports/ViewPriceToast.tsx'
        )
);

const viewerFragment = graphql`
    fragment PdpRespUnavailableContainer_viewer on Viewer {
        isWordpressArticlesEnabled: isEnabledByLocale(featureName: "WP_EDITORIAL")
        ...usePdpSharedTracking_viewer
        ...SharedRespLinksBlock_viewer
        ...PdpRespUnavailableDetailsTabs_viewer
        ...PdpRespSharedHeroCarousel_viewer
        ...PdpRespSharedSellerChatLoader_viewer @include(if: $fetchChat)
        ...PdpSharedSellerItemsCarousel_viewer @arguments(useML: false)
        ...PdpRespRefetchableSimilarItemsGrid_viewer @arguments(itemId: $itemId)
        ...PdpSharedFeaturedRelatedItemsCarouselLazy_viewer @include(if: $afterDisplayAndBtNone)
        ...PdpRespFeaturedItemsGridLazy_viewer @include(if: $afterDisplayAndBtNone)
        ...PdpRespSharedSidebarSellerInfoAndContact_viewer @include(if: $afterDisplay)
    }
`;
const itemFragment = graphql`
    fragment PdpRespUnavailableContainer_item on Item {
        serviceId
        seller {
            serviceId
            isAccessible
            ...PdpRespSharedSidebarSellerInfoAndContact_seller @include(if: $afterDisplay)
        }
        internalBuyLinks {
            ...SharedRespLinksBlock_links
        }
        storyTellingModule(shortTitles: true) {
            tabs {
                supplementaryEditorial {
                    articles {
                        __typename
                    }
                }
            }
        }
        ...PdpRespSharedTitle_item
        ...usePdpSharedTracking_item
        ...SharedRespLinksBlock_item
        ...PdpRespSharedBreadcrumbs_item
        ...PdpRespUnavailableDetailsTabs_item
        ...PdpRespSharedHeroCarousel_item
        ...PdpSharedEditorialArticles_refactor_item
        ...PdpRespSharedPrintDetailsLazy_item
        ...PriceDisplay_item
            @include(if: $afterDisplayOrBtSsr)
            @arguments(
                userId: $userId
                isTrade: $isTrade
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
                catchNonFatalErrors: false
            )
        ...PdpSharedItemFavorites_item @include(if: $afterDisplay)
        ...PdpRespSharedHeroCarouselPrint_item @include(if: $afterDisplay)
        ...PdpRespSharedSidebarSellerInfoAndContact_item @include(if: $afterDisplay)
        ...PdpSharedSellerItemsCarousel_item
        ...PdpSharedUnavailableViewPrice_item
        ...PdpRespRefetchableSimilarItemsGrid_item
        ...PdpRespFeaturedItemsGridLazy_item @include(if: $afterDisplayAndBtNone)
    }
`;
const userFragment = graphql`
    fragment PdpRespUnavailableContainer_user on User {
        serviceId
        ...PdpRespUnavailableDetailsTabs_user
        ...PdpRespSharedHeroCarousel_user
        ...PdpRespSharedPrintDetailsLazy_user
        ...PdpSharedItemFavorites_user @include(if: $afterDisplay)
        ...PdpRespSharedSidebarSellerInfoAndContact_user @include(if: $afterDisplay)
        ...PdpSharedSellerItemsCarousel_user
        ...PdpRespRefetchableSimilarItemsGrid_user
        ...PdpSharedFeaturedRelatedItemsCarouselLazy_user @include(if: $afterDisplayAndBtNone)
        ...PdpRespFeaturedItemsGridLazy_user @include(if: $afterDisplayAndBtNone)
    }
`;

export const PdpRespUnavailableContainer: FC<{
    item: Item;
    viewer: Viewer;
    user: User | null;
}> = ({ item: itemRef, user: userRef, viewer: viewerRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const {
        isSsrOnlyBot,
        isBot,
        isBannerAdsAbTestVariant,
        isTablet,
        isItemFavoritingFeb2026Variant,
        isPdpBottomNewSlModuleTestVariant,
    } = useServerVarsContext();

    const {
        currency,
        relayVars: {
            afterDisplay,
            fetchChat,
            userId: relayUserId,
            itemId: relayItemId,
            isTrade,
            priceBookName,
            fetchUser,
        },
    } = useRespUnavailableContext();
    const [viewPriceToast, setViewPriceToast] = useState(false);
    const detailTabsState = useUnavailableDetailTabsState();

    const hasEditorialArticles =
        !!item?.storyTellingModule?.tabs?.[0]?.supplementaryEditorial?.articles?.length;

    usePdpSharedTracking({ item, viewer });

    const { serviceId: userId } = user || {};
    const { isWordpressArticlesEnabled } = viewer;
    const { seller, serviceId: itemId, internalBuyLinks } = item;

    useDwelledOnPage(PAGE_TYPE.PDP);

    const similarItemsRef = useRef(null);

    const scrollToBuyerIncentives = useRef(true);
    const detailsSectionRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const displayBuyerIncentivesParam = getQueryParam(
            location.search,
            'displayBuyerIncentives'
        );
        const shouldScrollToBuyerIncentivesDetails =
            scrollToBuyerIncentives.current && displayBuyerIncentivesParam === 'true';
        if (shouldScrollToBuyerIncentivesDetails) {
            const detailsSection = detailsSectionRef?.current;
            if (detailsSection) {
                const [, setActiveTab] = detailTabsState;
                setActiveTab(BUYER_INCENTIVES);
                scrollToBuyerIncentives.current = false;
                scrollIntoView(detailsSection, { time: 500 }, () => detailsSection.focus());
            }
        }
    }, [detailTabsState]);

    useEffect(() => {
        trackBannerAdsAbTestVariant();
    }, []);

    const priceDisplay = (
        <PriceDisplay
            sku={null}
            useSvgIcon
            item={item}
            displayVatLabel
            currency={currency}
            hasPriceBlur={!userId}
            showSoldListPrice={false}
            transaction={null}
        />
    );

    return (
        <>
            <div className={styles.printHide}>
                <PdpRespSharedBreadcrumbs item={item} />
            </div>
            <div className="rowFlex" data-tn="pdp-main-container">
                <div
                    className={`${styles.topContainer} ${styles.carouselContainer} colXs12 colLg6 colLgOffset1 colXl6 colXlOffset1`}
                >
                    <div className={styles.printHide}>
                        <BrowserHistoryContextProvider>
                            <PdpRespSharedHeroCarousel
                                viewer={viewer}
                                item={item}
                                user={user}
                                currency={currency}
                                afterDisplay={afterDisplay}
                                size="xxSmall"
                            />
                        </BrowserHistoryContextProvider>
                        {isItemFavoritingFeb2026Variant && (
                            <div
                                className={classnames(
                                    styles.printHide,
                                    styles.favoritesContainer,
                                    dibsCss.absolute,
                                    dibsCss.topMedium,
                                    dibsCss.rightXxlarge,
                                    dibsCss.prSmaller
                                )}
                            >
                                <ClientSuspense fallback={null}>
                                    <PdpSharedItemFavorites
                                        item={item}
                                        user={user}
                                        showHeartCount
                                        wrapperClass={styles.favoritesWrapper}
                                        heartIconWrapperClass={styles.heartIconWrapper}
                                        animateHeartPulse={false}
                                        showNudge
                                        theme="circle-frosted-medium"
                                    />
                                </ClientSuspense>
                            </div>
                        )}
                    </div>
                    <ClientSuspense fallback={null}>
                        <div className={styles.printShow}>
                            <PdpRespSharedHeroCarouselPrint item={item} />
                        </div>
                    </ClientSuspense>
                </div>
                <div className={`${styles.topContainer} colXs12 colLg4 colXl4`}>
                    <div className={styles.sidebarContainer}>
                        <div className="rowFlex">
                            <div
                                className={classnames(
                                    'colXs12',
                                    !isItemFavoritingFeb2026Variant && 'colLg10'
                                )}
                            >
                                <PdpRespSharedTitle item={item} isUnavailablePdp />
                                <div className={styles.printHide}>
                                    {isSsrOnlyBot ? (
                                        priceDisplay
                                    ) : (
                                        <ClientSuspense fallback={null}>
                                            {priceDisplay}
                                        </ClientSuspense>
                                    )}
                                </div>
                            </div>
                            {!isItemFavoritingFeb2026Variant && (
                                <div
                                    className={`${styles.printHide} ${styles.favoritesContainer} colXs2`}
                                >
                                    <ClientSuspense fallback={null}>
                                        <PdpSharedItemFavorites
                                            item={item}
                                            user={user}
                                            showHeartCount
                                            wrapperClass={styles.favoritesWrapper}
                                            heartIconWrapperClass={styles.heartIconWrapper}
                                        />
                                    </ClientSuspense>
                                </div>
                            )}
                        </div>
                        <ClientSuspense fallback="">
                            {!userId && (
                                <PdpSharedUnavailableViewPrice
                                    item={item}
                                    currency={currency}
                                    launchOrigin="sold gating pdp sidebar"
                                    setViewPriceToast={setViewPriceToast}
                                />
                            )}
                        </ClientSuspense>
                        {seller?.isAccessible && (
                            <ClientSuspense fallback={null}>
                                <div
                                    className={classnames(
                                        dibsCss.myXsmall,
                                        styles.topDivider,
                                        styles.printHide
                                    )}
                                >
                                    <PdpRespSharedSidebarSellerInfoAndContact
                                        template="UNAVAILABLE"
                                        viewer={viewer}
                                        user={user}
                                        item={item}
                                        seller={item.seller}
                                        currency={currency}
                                        isClient={afterDisplay}
                                    />
                                </div>
                            </ClientSuspense>
                        )}
                        <PdpRespSharedQrCodeLazy itemId={itemId} className={styles.printShow} />
                    </div>
                </div>
            </div>

            {isBannerAdsAbTestVariant && (
                <SharedBillboardAd
                    placement={PDP_SOLD_TOP_BILLBOARD_PLACEMENT}
                    className={dibsCss.myLarge}
                />
            )}

            <div ref={detailsSectionRef} className={styles.printHide}>
                <PdpRespUnavailableDetailsTabs
                    user={user}
                    item={item}
                    viewer={viewer}
                    tabsState={detailTabsState}
                />
            </div>
            <div className={styles.printShow}>
                <PdpRespSharedPrintDetailsLazy item={item} user={user} currency={currency} />
            </div>

            {fetchChat && <PdpRespSharedSellerChatLoader viewer={viewer} currency={currency} />}

            <div className={styles.printHide}>
                <div ref={similarItemsRef}>
                    <PdpRespRefetchableSimilarItemsGrid
                        viewer={viewer}
                        user={user}
                        item={item}
                        header={youMayAlsoLike}
                        relayVariables={{ isClient: afterDisplay }}
                        currency={currency}
                        favoritesLocation="SIMILAR_ITEM_GRID"
                        gridLocation="SIMILAR_ITEM_GRID"
                        dataTn="pdp-similar-recommendation-grid"
                        isDark={false}
                        hideBar
                        gridItemWrapperClass={styles.similarItem}
                    />
                </div>
                <PdpSharedSellerItemsCarousel
                    viewer={viewer}
                    item={item}
                    user={user}
                    currency={currency}
                />

                {/* Featured Related Items */}
                <PdpSharedFeaturedRelatedItemsCarouselLazy
                    viewer={viewer}
                    user={user}
                    afterDisplay={afterDisplay}
                    currency={currency}
                    onCarouselVisible={trackPdpBottomNewSlModuleTest}
                    carouselCapacity={isPdpBottomNewSlModuleTestVariant ? 5 : 10}
                />

                {isBannerAdsAbTestVariant && (
                    <SharedBillboardAd
                        placement={PDP_SOLD_BOTTOM_BILLBOARD_PLACEMENT}
                        className={dibsCss.myLarge}
                    />
                )}

                {afterDisplay && userId && (
                    <PdpSharedRecentlyFavoritedCarouselRenderer
                        userId={relayUserId}
                        itemId={relayItemId}
                        isTrade={!!isTrade}
                        priceBookName={priceBookName || ''}
                        fetchUser={!!fetchUser}
                        currency={currency}
                    />
                )}

                <RecentlyViewedItemsProvider
                    excludedItemIds={item?.serviceId ? [item?.serviceId] : null}
                    deviceType={isTablet ? TABLET : DESKTOP}
                >
                    <RecentlyViewedItemsLazy
                        isBot={isBot}
                        authModalLoader={authModalLoader}
                        hasTopSeparator
                        hasBottomSeparator={
                            hasEditorialArticles && !!viewer.isWordpressArticlesEnabled
                        }
                    />
                </RecentlyViewedItemsProvider>

                <PdpRespFeaturedItemsGridLazy
                    isUnavailablePdp
                    viewer={viewer}
                    user={user}
                    item={item}
                    afterDisplay={afterDisplay}
                    currency={currency}
                />
            </div>

            {isWordpressArticlesEnabled && (
                <div className={styles.printHide}>
                    <PdpSharedEditorialArticles item={item} />
                </div>
            )}

            <div className={styles.printHide}>
                <SharedRespLinksBlock
                    item={item}
                    viewer={viewer}
                    itemSearch={null}
                    onClick={() => {
                        trackEvent({
                            category: 'product',
                            action: 'more ways to browse link click',
                        });
                    }}
                    links={internalBuyLinks?.filter(filterNulls) || null}
                />
            </div>

            {viewPriceToast && (
                <ClientSuspense fallback="">
                    <ViewPriceToast
                        isOpen={viewPriceToast}
                        onClose={() => setViewPriceToast(false)}
                    />
                </ClientSuspense>
            )}
        </>
    );
};
